import { ApplicationStateEnumOptions } from "@boundless/bl-api-client/lender/models/ApplicationStateEnumLabels"
import { ApplicationStatusEnumOptions } from "@boundless/bl-api-client/lender/models/ApplicationStatusEnumLabels"
import { useLenderApplicationsListSuspense } from "@boundless/bl-api-client/lender/services"
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table"
import DatePicker from "react-datepicker"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { applicationColumns } from "@/lib/data-tables/lender/application-columns"

import type { Application } from "@boundless/bl-api-client/lender/models"

export function ApplicationsTable() {
  const { data: applications } = useLenderApplicationsListSuspense()

  const table = useReactTable<Application>({
    data: applications,
    columns: applicationColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: "submittedAt", desc: true }],
    },
  })

  const getDateFilter = (
    id: string,
  ): { from: Date | null; to: Date | null } => {
    return (
      (table.getColumn(id)?.getFilterValue() as {
        from: Date | null
        to: Date | null
      }) ?? {
        from: null,
        to: null,
      }
    )
  }

  const setDateFilter = (
    id: string,
    updates: Partial<{ from: Date | null; to: Date | null }>,
  ): void => {
    const current = getDateFilter(id)
    table.getColumn(id)?.setFilterValue({
      from: updates.from ?? current.from,
      to: updates.to ?? current.to,
    })
  }

  const companyNamesOptions = Array.from(
    new Set(applications.map((item) => item.companyName)),
  ).map((name) => ({
    value: name,
    label: name,
  }))

  const stateOptions = ApplicationStateEnumOptions.filter(({ value }) =>
    applications.some((app) => app.state === value),
  )

  const statusOptions = ApplicationStatusEnumOptions.filter(({ value }) =>
    applications.some((app) => app.status === value),
  )

  return (
    <>
      <div className="mb-4 grid grid-cols-8 gap-4">
        <div className="space-y-2">
          <Label className="text-base font-medium">Company</Label>
          <Select
            value={
              (table.getColumn("companyName")?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table.getColumn("companyName")?.setFilterValue(value)
            }
          >
            <SelectTrigger className="!h-9 w-full">
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {companyNamesOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label className="text-base font-medium">Last Modified</Label>
          <div className="flex space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("lastModifiedAt").from}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { from: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("lastModifiedAt").to}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { to: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="col-span-2 space-y-2">
          <Label className="text-base font-medium">Submitted</Label>
          <div className="flex space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("submittedAt").from}
                onChange={(date) =>
                  setDateFilter("submittedAt", { from: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="!w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("submittedAt").to}
                onChange={(date) => setDateFilter("submittedAt", { to: date })}
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">State</Label>
          <Select
            value={(table.getColumn("state")?.getFilterValue() as string) ?? ""}
            onValueChange={(value) =>
              table.getColumn("state")?.setFilterValue(value)
            }
          >
            <SelectTrigger className="!h-9 w-full">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {stateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value ?? ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">Status</Label>
          <Select
            value={
              (table.getColumn("status")?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table
                .getColumn("status")
                ?.setFilterValue(value === "all" ? undefined : value)
            }
          >
            <SelectTrigger className="!h-9 w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All</SelectItem>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value ?? ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <Button
            variant="outline"
            className="h-9 w-full"
            onClick={() => table.resetColumnFilters()}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className="w-40">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={applicationColumns.length}
                className="h-24 text-center"
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  )
}

export function FallbackApplicationsTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Last Modified</TableHead>
          <TableHead>Submitted</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Details</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 3 }).map((_, i) => (
          <TableRow key={i}>
            {Array.from({ length: applicationColumns.length }).map((_, j) => (
              <TableCell key={j}>
                <Skeleton className="h-6" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
